import { transformUrnToWitboostId } from '@agilelab/plugin-wb-builder-common';
import {
  createNameWithVersionMap,
  GenericElement,
  NotFoundException,
  Pagination,
  SearchFilter,
  TableCellProps,
  WbResponsiveTableToolbar,
} from '@agilelab/plugin-wb-platform';
import { practiceShaperApiRef } from '@agilelab/plugin-wb-practice-shaper';
import { parseEntityRef } from '@mainstage/catalog-model';
import { useApi } from '@mainstage/core-plugin-api';
import { CatalogApi } from '@mainstage/plugin-catalog-react';
import { FC, ReactNode, useMemo, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { contextProviderFactory } from '../scopeProvider.factory';
import {
  MyProvider,
  ProviderFilters,
  ScopeProviderType,
  SystemFilters,
} from '../types';
import { getLabelAndValue } from '../../utils';

export type ComponentTypePSElement = GenericElement & {};

interface ComponentTypeContextType
  extends ScopeProviderType<GenericElement, ProviderFilters> {}

const [useComponentTypeContext, ComponentTypeContextProvider] =
  contextProviderFactory<ComponentTypeContextType>();

export default useComponentTypeContext;

export interface ComponentTypeProviderProps {
  children: ReactNode;
}

export const ComponentTypeFiltersSection = () => {
  const { filters, changeFilters, resetFilters } = useComponentTypeContext();

  return (
    <WbResponsiveTableToolbar
      onClear={resetFilters}
      style={{ marginTop: '16px' }}
      marginBottom
    >
      <WbResponsiveTableToolbar.Filters>
        <SearchFilter
          key="search"
          value={filters.search ?? ''}
          changeFilters={changeFilters}
        />
      </WbResponsiveTableToolbar.Filters>
    </WbResponsiveTableToolbar>
  );
};

export const ComponentTypeProvider: FC<ComponentTypeProviderProps> = props => {
  const { children } = props;
  const [filters, setFilters] = useState<SystemFilters>({});
  const [count, setCount] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 25,
    currentPage: 0,
  });
  const practiceShaperApi = useApi(practiceShaperApiRef);
  const defaultColumns: TableCellProps<GenericElement>[] = useMemo(() => {
    return [
      {
        headerName: 'Name',
        field: 'displayName',
        uniqueFieldId: 'displayName',
      },
      {
        headerName: 'Description',
        field: 'description',
        uniqueFieldId: 'description',
      },
    ];
  }, []);

  const componentTypeState = useAsync(async () => {
    const result = await practiceShaperApi.getComponentTypes({
      filter: { isAbstract: false },
    });

    setCount(result.items.length);

    const adaptedValues = result.items.map(entity => getLabelAndValue(entity));

    const nameWithVersionMap = createNameWithVersionMap(
      adaptedValues,
      'displayName',
    );

    return adaptedValues.map(val => {
      return {
        ...val,
        displayName: nameWithVersionMap[val.id].nameWithVersion,
      };
    });
  }, [filters]);

  const processedData = useMemo(() => {
    if (!componentTypeState.value) {
      return { loading: false, error: null, value: [] };
    }

    const filteredData = componentTypeState.value.filter(
      item =>
        item.name
          ?.toLowerCase()
          .includes(filters.search?.toLowerCase() ?? '') ||
        item.description
          ?.toLowerCase()
          .includes(filters.search?.toLowerCase() ?? ''),
    );

    const startIndex = pagination.currentPage * pagination.limit;
    const endIndex = startIndex + pagination.limit;

    return {
      loading: componentTypeState.loading,
      error: componentTypeState.error,
      value: filteredData.slice(startIndex, endIndex),
    };
  }, [
    componentTypeState.value,
    componentTypeState.loading,
    componentTypeState.error,
    filters.search,
    pagination.currentPage,
    pagination.limit,
  ]);

  const getRowId = (row: any) => row.id;

  return (
    <ComponentTypeContextProvider
      value={
        {
          entitiesState: processedData,
          columns: defaultColumns,
          getRowId,
          filters,
          changeFilters: <K extends keyof SystemFilters>(
            key: K,
            filterValue: SystemFilters[K],
          ) => {
            setPagination(p => ({ ...p, currentPage: 0 }));
            setFilters(f => ({ ...f, [key]: filterValue }));
          },
          resetFilters: () => {
            setFilters({});
          },
          pagination,
          setPagination,
          count,
        } as unknown as ScopeProviderType<GenericElement, ProviderFilters>
      }
    >
      {children}
    </ComponentTypeContextProvider>
  );
};

export class ComponentTypeHandlerProvider
  implements MyProvider<GenericElement, ProviderFilters>
{
  private readonly catalogApi: CatalogApi;

  constructor(catalogApi: CatalogApi) {
    this.catalogApi = catalogApi;
  }

  useContext = useComponentTypeContext;

  renderContextProvider(children: ReactNode) {
    return <ComponentTypeProvider>{children}</ComponentTypeProvider>;
  }

  filtersSection = ComponentTypeFiltersSection;

  async resolve(ref: string) {
    const resolvedRef = transformUrnToWitboostId(ref) ?? ref;
    const parsedEntityRef = parseEntityRef(resolvedRef);
    try {
      if (parsedEntityRef.kind === 'componenttype') {
        const entity = await this.catalogApi.getEntityByRef(resolvedRef);
        if (entity) return getLabelAndValue(entity);
        throw new NotFoundException(
          `Cannot find a Component type entity with ref: ${ref}`,
        );
      }

      return undefined;
    } catch (error) {
      throw new Error(`Error while resolving entity with ref: ${ref}`);
    }
  }
}
